import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Building2, Mail, MapPin, Phone, User, Shield, Globe, FileText, Briefcase } from 'lucide-react'
import toast from 'react-hot-toast'
import { authService } from '../../../services/authService'

const CompanyDetailsForm = React.forwardRef(({
  formData = {},
  onNext,
  onPrevious,
  isLoading,
  userType = 'company',
  userId,
  companyId,
  userData
}, ref) => {
  const [formState, setFormState] = useState({
    // Company Information
    company_name: '',
    legal_entity_name: '',
    corporate_email: '',
    pan_tin: '',
    gstin: '',
    business_domain: '',
    company_size: '',
    company_description: '',
    website_url: '',

    // Contact Information
    contact_person_name: '',
    contact_designation: '',
    contact_email: '',
    contact_phone: '',

    // Address Information (often required)
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',

    ...formData
  })

  const [errors, setErrors] = useState({})
  const [companySizeOptions, setCompanySizeOptions] = useState([])
  const [companySizeLoading, setCompanySizeLoading] = useState(true)

  // Pre-fill user data if available - checks if userData is non-empty object
  useEffect(() => {
    if (userData && Object.keys(userData).length > 0) {
      setFormState(prev => ({
        ...prev,
        contact_person_name: userData.full_name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim(),
        contact_designation: userData.designation || '',
        contact_email: userData.email || '',
        corporate_email: userData.corporate_email || userData.email || '',
        company_name: userData.company_name || '',
        contact_phone: userData.phone_number || userData.phone || ''
      }))
    }
  }, [userData])

  const validateUrl = (url) => {
    if (!url.trim()) return true // Empty is valid (optional field)

    try {
      // Basic URL validation
      const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/
      return urlPattern.test(url)
    } catch {
      return false
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Required fields validation
    const requiredFields = {
      company_name: 'Company name',
      legal_entity_name: 'Legal entity name',
      corporate_email: 'Corporate email',
      contact_person_name: 'Contact person name',
      contact_email: 'Contact email'
    }

    for (const [field, label] of Object.entries(requiredFields)) {
      if (!formState[field]?.trim()) {
        newErrors[field] = `${label} is required`
      }
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (formState.corporate_email && !emailRegex.test(formState.corporate_email)) {
      newErrors.corporate_email = 'Please enter a valid corporate email address'
    }

    if (formState.contact_email && !emailRegex.test(formState.contact_email)) {
      newErrors.contact_email = 'Please enter a valid contact email address'
    }

    // URL validation
    if (formState.website_url && !validateUrl(formState.website_url)) {
      newErrors.website_url = 'Please enter a valid website URL (e.g., https://example.com)'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormState(prev => ({
      ...prev,
      [name]: value
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const formatWebsiteUrl = (url) => {
    if (!url.trim()) return ''

    // If URL doesn't start with http:// or https://, add https://
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`
    }

    return url
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {

      if (!validateForm()) {
        toast.error('Please fix the validation errors before submitting')
        return
      }

      // Prepare data for API - match backend expectations
      const submissionData = {
        // Company info
        company_name: formState.company_name.trim(),
        legal_entity_name: formState.legal_entity_name.trim() || formState.company_name.trim(),
        corporate_email: formState.corporate_email.trim().toLowerCase(),
        business_domain: formState.business_domain.trim(),
        company_size: formState.company_size,
        company_description: formState.company_description.trim(),
        website_url: formState.website_url ? formatWebsiteUrl(formState.website_url.trim()) : '',

        // Tax/legal info
        pan_tin: formState.pan_tin.trim(),
        gstin: formState.gstin.trim(),

        // Contact info
        contact_person_name: formState.contact_person_name.trim(),
        contact_designation: formState.contact_designation.trim(),
        contact_email: formState.contact_email.trim().toLowerCase(),
        contact_phone: formState.contact_phone.trim(),

        // Address info
        address_line_1: formState.address_line_1.trim(),
        address_line_2: formState.address_line_2.trim(),
        city: formState.city.trim(),
        state: formState.state.trim(),
        country: formState.country.trim(),
        postal_code: formState.postal_code.trim(),

        // System fields
        user_id: userId,
        user_type: userType,
        registration_step: 1
      }

      await onNext(submissionData)

    } catch (error) {
      console.error('❌ Error in form submission:', error)
      toast.error(error.message || 'Failed to save company details. Please try again.')
    }
  }

  // Expose submit function via ref
  React.useImperativeHandle(ref, () => ({
    submitForm: () => {
      const form = document.querySelector('form')
      if (form) {
        const submitEvent = new Event('submit', { bubbles: true, cancelable: true })
        form.dispatchEvent(submitEvent)
      }
    }
  }))

  // Company size options
  useEffect(() => {
    let mounted = true
    const fetchCompanySizes = async () => {
      try {
        setCompanySizeLoading(true)
        const response = await authService.getEnum('company_size')
        if (response && response.success && response.data) {
          // response.data is an array of { key, value }
          if (mounted) setCompanySizeOptions(response.data)
        } else {
          if (mounted) setCompanySizeOptions([])
        }
      } catch (err) {
        console.error('Failed to fetch company sizes:', err)
        if (mounted) setCompanySizeOptions([])
      } finally {
        if (mounted) setCompanySizeLoading(false)
      }
    }

    fetchCompanySizes()

    return () => { mounted = false }
  }, [])

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Company Basic Information */}
        <div className="md:col-span-2">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
            <Building2 className="w-5 h-5 mr-2 text-blue-600" />
            Company Information
          </h3>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Company Name *
          </label>
          <input
            type="text"
            name="company_name"
            value={formState.company_name}
            onChange={handleChange}
            required
            className={`w-full px-2.5 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white ${errors.company_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            placeholder="Enter company name"
          />
          {errors.company_name && (
            <p className="mt-1 text-xs text-red-600">{errors.company_name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Legal Entity Name *
          </label>
          <input
            type="text"
            name="legal_entity_name"
            value={formState.legal_entity_name}
            onChange={handleChange}
            required
            className={`w-full px-2.5 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white ${errors.legal_entity_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            placeholder="Legal name as per registration documents"
          />
          {errors.legal_entity_name && (
            <p className="mt-1 text-xs text-red-600">{errors.legal_entity_name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Corporate Email *
          </label>
          <input
            type="email"
            name="corporate_email"
            value={formState.corporate_email}
            onChange={handleChange}
            required
            className={`w-full px-2.5 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white ${errors.corporate_email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            placeholder="corporate@company.com"
          />
          {errors.corporate_email && (
            <p className="mt-1 text-xs text-red-600">{errors.corporate_email}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Business Domain
          </label>
          <input
            type="text"
            name="business_domain"
            value={formState.business_domain}
            onChange={handleChange}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="e.g., Technology, Healthcare, Finance"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Company Size
          </label>
          <select
            name="company_size"
            value={formState.company_size}
            onChange={handleChange}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="">Select company size</option>
            {companySizeLoading ? (
              <option disabled>Loading sizes...</option>
            ) : companySizeOptions.length === 0 ? (
              <option disabled>No sizes available</option>
            ) : (
              companySizeOptions.map((item) => (
                <option key={item.key} value={item.key}>{item.value}</option>
              ))
            )}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center">
            <Globe className="w-4 h-4 mr-1" />
            Website URL
          </label>
          <input
            type="text"
            name="website_url"
            value={formState.website_url}
            onChange={handleChange}
            className={`w-full px-2.5 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white ${errors.website_url ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            placeholder="example.com"
          />
          {errors.website_url && (
            <p className="mt-1 text-xs text-red-600">{errors.website_url}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center">
            <Shield className="w-4 h-4 mr-1" />
            PAN/TIN
          </label>
          <input
            type="text"
            name="pan_tin"
            value={formState.pan_tin}
            onChange={handleChange}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="PAN or TIN number"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center">
            <FileText className="w-4 h-4 mr-1" />
            GSTIN
          </label>
          <input
            type="text"
            name="gstin"
            value={formState.gstin}
            onChange={handleChange}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="GSTIN number"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Company Description
          </label>
          <textarea
            name="company_description"
            value={formState.company_description}
            onChange={handleChange}
            rows={4}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="Describe your company's business, products, and services..."
          />
        </div>

        {/* Contact Person Information */}
        <div className="md:col-span-2 mt-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
            <User className="w-5 h-5 mr-2 text-green-600" />
            Contact Information
          </h3>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Contact Person Name *
          </label>
          <input
            type="text"
            name="contact_person_name"
            value={formState.contact_person_name}
            onChange={handleChange}
            required
            className={`w-full px-2.5 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white ${errors.contact_person_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            placeholder="Full name of contact person"
          />
          {errors.contact_person_name && (
            <p className="mt-1 text-xs text-red-600">{errors.contact_person_name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center">
            <Briefcase className="w-4 h-4 mr-1" />
            Designation
          </label>
          <input
            type="text"
            name="contact_designation"
            value={formState.contact_designation}
            onChange={handleChange}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="e.g., Director, Manager"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center">
            <Mail className="w-4 h-4 mr-1" />
            Contact Email *
          </label>
          <input
            type="email"
            name="contact_email"
            value={formState.contact_email}
            onChange={handleChange}
            required
            className={`w-full px-2.5 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white ${errors.contact_email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            placeholder="contact.person@example.com"
          />
          {errors.contact_email && (
            <p className="mt-1 text-xs text-red-600">{errors.contact_email}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center">
            <Phone className="w-4 h-4 mr-1" />
            Contact Phone
          </label>
          <input
            type="tel"
            name="contact_phone"
            value={formState.contact_phone}
            onChange={handleChange}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="+1 (555) 123-4567"
          />
        </div>

        {/* Address Information */}
        <div className="md:col-span-2 mt-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
            <MapPin className="w-5 h-5 mr-2 text-purple-600" />
            Address Information
          </h3>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Address Line 1
          </label>
          <input
            type="text"
            name="address_line_1"
            value={formState.address_line_1}
            onChange={handleChange}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="Street address, P.O. box"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Address Line 2
          </label>
          <input
            type="text"
            name="address_line_2"
            value={formState.address_line_2}
            onChange={handleChange}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="Apartment, suite, etc."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            City
          </label>
          <input
            type="text"
            name="city"
            value={formState.city}
            onChange={handleChange}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="City"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            State
          </label>
          <input
            type="text"
            name="state"
            value={formState.state}
            onChange={handleChange}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="State/Province"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Country
          </label>
          <input
            type="text"
            name="country"
            value={formState.country}
            onChange={handleChange}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="Country"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Postal Code
          </label>
          <input
            type="text"
            name="postal_code"
            value={formState.postal_code}
            onChange={handleChange}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="ZIP/Postal code"
          />
        </div>
      </div>

      {/* Hidden submit button for ref access */}
      <button type="submit" className="hidden">
        Submit
      </button>
    </form>
  )
})

CompanyDetailsForm.displayName = 'CompanyDetailsForm'

export default CompanyDetailsForm